import axios from 'axios';
import { apiUrl } from './config';
import { getAuthToken, getPortalType } from '@/utils/auth';

/**
 * Chat API Service for Budlee AI Chat Feature
 * Uses /api/v1/chat/sessions/ endpoints
 */

/**
 * Create a new chat session
 * @param {Object} params - Session parameters
 * @param {number} params.subject_id - Subject ID (optional)
 * @param {number} params.chapter_id - Chapter ID (optional)
 * @param {string} params.page_context - Page context (chat, learn, curiosity, etc.)
 * @param {string} params.explanation_mode - Explanation mode (textbook, detailed, beyond_book) - only for 'chat' context
 * @param {number} params.curiosity_content_id - Content ID for curiosity contexts (optional)
 * @param {number} params.topic_id - Topic ID (optional)
 * @param {number} params.news_id - News ID for news contexts (optional)
 * @returns {Promise<Object>} Session object with session_id
 */
export const createChatSession = async (params) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await axios.post(
    apiUrl('v1/chat/sessions/'),
    params,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
};

/**
 * Send a message in a chat session
 * @param {string} sessionId - Session UUID
 * @param {string} content - Message content
 * @param {AbortSignal} signal - Optional AbortSignal for cancelling the request
 * @param {number} teacherNoteId - Optional teacher note ID for class notes context
 * @param {Object} anchor - Optional selection anchor metadata (e.g., from PDF selection)
 * @returns {Promise<Object>} Message object with answer, key_points, suggested_questions, etc.
 */
// NOTE: `studentClass` is optional and is interpreted as grade/class level (1-12).
export const sendChatMessage = async (
  sessionId,
  content,
  signal = null,
  teacherNoteId = null,
  anchor = null,
  explanationMode = null,
  studentClass = null
) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  if (!token) {
    throw new Error('Authentication required');
  }

  // Build payload with content and optional teacher_note_id
  const payload = { content };
  if (teacherNoteId !== null) {
    payload.teacher_note_id = teacherNoteId;
  }
  if (anchor !== null && anchor !== undefined) {
    payload.anchor = anchor;
  }
  if (explanationMode) {
    payload.explanation_mode = explanationMode;
  }
  if (studentClass !== null && studentClass !== undefined && studentClass !== '') {
    payload.student_class = Number(studentClass);
  }

  // Build config with headers and optional abort signal
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };

  // Add abort signal if provided
  if (signal) {
    config.signal = signal;
  }

  const response = await axios.post(
    apiUrl(`v1/chat/sessions/${sessionId}/messages/`),
    payload,
    config
  );

  return response.data;
};

/**
 * Send a message in a chat session with streaming (SSE).
 * Backend: POST /v1/chat/sessions/:id/messages/?stream=1 with `text/event-stream` response.
 *
 * Emits events:
 * - `delta`: { text }
 * - `final`: full message payload (same as non-stream endpoint)
 * - `cancel`: { status, detail }
 * - `error`: { message }
 */
export const sendChatMessageStream = async (
  sessionId,
  payload,
  {
    signal = null,
    onDelta = null,
    onReplace = null,
    onEvent = null,
  } = {}
) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  if (!token) {
    throw new Error('Authentication required');
  }

  const res = await fetch(apiUrl(`v1/chat/sessions/${sessionId}/messages/?stream=1`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream'
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!res.ok) {
    let detail = '';
    try {
      detail = await res.text();
    } catch (e) {}
    const err = new Error(detail || `Streaming request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }

  const contentType = (res.headers.get('content-type') || '').toLowerCase();
  if (!contentType.includes('text/event-stream')) {
    // Fallback: server didn't stream; try to parse JSON.
    const data = await res.json();
    return data;
  }

  if (!res.body) {
    throw new Error('Streaming not supported (no response body)');
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  const emit = (eventName, data) => {
    try {
      onEvent && onEvent(eventName, data);
    } catch (e) {}
    if (eventName === 'delta') {
      try {
        onDelta && onDelta(data);
      } catch (e) {}
    }
    if (eventName === 'replace') {
      try {
        onReplace && onReplace(data);
      } catch (e) {}
    }
  };

  const parseEventBlock = (block) => {
    const lines = block.split('\n');
    let eventName = 'message';
    let dataLines = [];
    for (const line of lines) {
      if (!line) continue;
      if (line.startsWith(':')) continue; // comment
      if (line.startsWith('event:')) {
        eventName = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice('data:'.length).trimStart());
      }
    }
    const dataStr = dataLines.join('\n');
    if (!dataStr) return null;
    let data;
    try {
      data = JSON.parse(dataStr);
    } catch (e) {
      data = { raw: dataStr };
    }
    return { eventName, data };
  };

  let finalPayload = null;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let idx;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);

      const parsed = parseEventBlock(block);
      if (!parsed) continue;

      emit(parsed.eventName, parsed.data);
      if (parsed.eventName === 'final') {
        finalPayload = parsed.data;
      }
      if (parsed.eventName === 'error') {
        const err = new Error(parsed.data?.message || 'Streaming error');
        err.data = parsed.data;
        throw err;
      }
      if (parsed.eventName === 'cancel') {
        const err = new Error(parsed.data?.detail || 'Generation cancelled');
        err.code = 'CANCELLED';
        err.data = parsed.data;
        throw err;
      }
    }
  }

  // If we never saw a final event, return null to let caller fall back.
  return finalPayload;
};

/**
 * Cancel chat generation and stop LLM processing
 * @param {string} sessionId - Session UUID
 * @returns {Promise<Object>} Status object
 */
export const cancelChatGeneration = async (sessionId) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await axios.post(
    apiUrl(`v1/chat/sessions/${sessionId}/cancel/`),
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
};

/**
 * List chat sessions
 * @param {Object} params - Query parameters
 * @param {string} params.page_context - Filter by page context (optional)
 * @param {number} params.page - Page number (optional)
 * @param {number} params.page_size - Page size (optional)
 * @returns {Promise<Object>} Paginated list of sessions
 */
export const listChatSessions = async (params = {}) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await axios.get(
    apiUrl('v1/chat/sessions/'),
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
};

/**
 * Get a chat session with all messages
 * @param {string} sessionId - Session UUID
 * @returns {Promise<Object>} Session object with messages array
 */
export const getChatSession = async (sessionId) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await axios.get(
    apiUrl(`v1/chat/sessions/${sessionId}/`),
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
};

/**
 * Get or create a session for a specific context
 * Helper function that checks for existing active session or creates a new one
 * @param {Object} params - Session parameters (same as createChatSession)
 * @param {boolean} createNew - Force create new session (default: false)
 * @returns {Promise<Object>} Session object
 */
export const getOrCreateSession = async (params, createNew = false) => {
  if (createNew) {
    return createChatSession(params);
  }

  // Try to find existing active session with same context
  try {
    const queryParams = {
      page_context: params.page_context,
      page_size: 100  // Get more to filter properly
    };

    const sessions = await listChatSessions(queryParams);

    if (sessions.results && sessions.results.length > 0) {
      // Filter for matching context based on page_context
      let activeSession = null;

      if (params.page_context === 'chat' || params.page_context === 'learn') {
        // For chat/learn contexts, match subject_id and chapter_id
        activeSession = sessions.results.find(s =>
          s.is_active &&
          s.subject_id === params.subject_id &&
          s.chapter_id === params.chapter_id
        );
      } else if (params.page_context === 'curiosity_video' || params.page_context === 'curiosity_podcast' || params.page_context === 'curiosity_content') {
        // For video/podcast/content contexts, match curiosity_content_id and also subject/chapter when present
        activeSession = sessions.results.find(s => {
          if (!s.is_active) return false;
          // If subject/chapter provided, match them as well to ensure correct curriculum context
          if ((params.subject_id || params.chapter_id) && (s.subject_id !== params.subject_id || s.chapter_id !== params.chapter_id)) {
            return false;
          }
          return s.curiosity_content_id === params.curiosity_content_id;
        });
      } else if (params.page_context === 'curiosity_topic_detail') {
        // For topic detail, match topic_id
        activeSession = sessions.results.find(s =>
          s.is_active &&
          s.topic_id === params.topic_id
        );
      } else if (params.page_context === 'curiosity_news_detail') {
        // For news detail, match news_id
        activeSession = sessions.results.find(s =>
          s.is_active &&
          s.news_id === params.news_id
        );
      } else {
        // For other contexts, just check is_active
        activeSession = sessions.results.find(s => s.is_active);
      }

      if (activeSession) {
        return { session_id: activeSession.session_id, ...activeSession };
      }
    }
  } catch (error) {
    console.warn('Failed to fetch existing sessions, creating new one:', error);
  }

  // Create new session if no active session found
  return createChatSession(params);
};

/**
 * Update a chat session's explanation_mode
 * @param {string} sessionId - Session UUID
 * @param {string} explanationMode - New explanation mode (textbook, detailed, beyond_book)
 * @returns {Promise<Object>} Updated session object
 */
export const updateSessionExplanationMode = async (sessionId, explanationMode) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await axios.patch(
    apiUrl(`v1/chat/sessions/${sessionId}/`),
    { explanation_mode: explanationMode },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
};

